import React, {useRef, memo, useCallback, useEffect, useMemo, useReducer, useState} from 'react'
import {Box, CircularProgress, Typography} from "@mui/material";
import {VariableSizeList} from 'react-window';
import AutoSizer from "react-virtualized-auto-sizer";

import {
    add,
    differenceInMonths,
    differenceInWeeks,
    eachMonthOfInterval,
    getMonth,
    getYear,
    isSunday,
    isSaturday,
    isToday,
    max,
    min,
    startOfDay,
    formatISO
} from "date-fns";

import {
    dayOfWeek,
    months,
    chunk,
    getNumberOfMondays,
    getSegments,
    getVisibleContents,
    inFirstSevenDaysOfMonth,
    sortEvents,
    monthVerticalVisibleDays,
    monthVerticalDayRange
} from '../utils';

import './monthVertical.css'
import {usePlanner} from "../index";
import MonthSelector from "../MonthSelector";
import useTimer from "../../../hooks/useTimer";

const weekRowHeight = 60;
const segmentHeight = 32;
const emptyArray = [];  // to avoid re-rendering when passing an empty array for events or dataGrid in the month

/*
 * The outer component that wrap all the elements: week day names and list of months.
 * Its main purpose is to wrap the virtualized list of months.
 */
const MonthVerticalView = function ({renderDayCell}) {
    const {minDate, maxDate, initialDate, setGoToDate, MonthDataLoader, setRenderDayCell} = usePlanner();
    const virtualizedListRef = useRef();
    const initialMonthIndex = differenceInMonths(initialDate, minDate);
    const [visibleDate, setVisibleDate] = useState(initialDate);

    const monthList = useMemo(
        () => eachMonthOfInterval({start: minDate, end: maxDate}),
        [minDate, maxDate]
    );

    /*
     * This function MUST be either memoized or defined outside this component because of the following reasons:
     * - this function is passed as a prop to the virtualized list, so every time the parent re-render
     *   the virtualized list will re-render when the children change.
     * - when the virtualized list rerender if the children (this function) is changed the list re-create all the items,
     *   so each month loader will be un-mounted and remounted.
     * The function cannot be defined outside the component because it uses the month list.
     *
     * The Month prop MUST be a component and not a function that return a component otherwise new months will be
     * created every time the DataLoader re-render.
     */
    const renderMonth = useCallback(
        ({index, style}) => {
            const month = getMonth(monthList[index]);
            const year = getYear(monthList[index]);
            // be careful, the first day of a month in the vertical view mode is the
            // first monday after the month start (if not monday), while the last day
            // is the last sunday after the month end (if not sunday)
            const [dateStart, dateEnd] = monthVerticalDayRange(month, year);
            return <MonthDataLoader month={month}
                                    year={year}
                                    dateStart={dateStart}
                                    dateEnd={dateEnd}
                                    style={style}
                                    Month={Month}
            />
        },
        [monthList]
    );

    const getMonthHeight = useCallback(
        (index) => getNumberOfMondays(monthList[index]) * weekRowHeight,
        [monthList]
    );

    // Compute the average month height of all the months from minDate to initialDate to get the correct initial offset
    const averageMonthHeight = useMemo(
        () => {
            const nMonths = differenceInMonths(initialDate, minDate);
            if(nMonths === 0){
                return weekRowHeight * 4.3 // Une the average number of weeks in one month 52 / 12 = 4.3
            }else{
                return differenceInWeeks(initialDate, minDate) * weekRowHeight / nMonths
            }
        },
        [minDate, initialDate]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    // Set the render day cell function to the planner context
    useEffect(() => {
        if(!!renderDayCell){
            setRenderDayCell(() => renderDayCell)
        }
    }, [renderDayCell, setRenderDayCell])

    // Set the goToDate function to the planner context
    useEffect(
        () => {
            const handleSelectMonth = (date) => {
                if(!!virtualizedListRef.current){
                    const itemIndex = differenceInMonths(max([min([date, maxDate]), minDate]), minDate);
                    virtualizedListRef.current.scrollToItem(itemIndex, "center")
                }
            }
            setGoToDate(() => handleSelectMonth)
        },
        []  // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Get the first visible month
    const handleOnItemsRendered = ({visibleStartIndex}) => {
        setVisibleDate(monthList[visibleStartIndex]);
    }

    const [scrollState, updateScrollState] = useReducer(
        (oldState, newState) => ({...oldState, ...newState}),
        {offset: initialMonthIndex * averageMonthHeight, fastScrolling: false}
    );
    const [startScrollTimer, restartScrollTimer] = useTimer(
        () => updateScrollState({fastScrolling: false}),
        300);

    const handleOnScroll = ({scrollOffset}) => {
        let fastScrolling = scrollState.fastScrolling;

        if (!scrollState.fastScrolling) {
            if (Math.abs(scrollOffset - scrollState.offset) > 200) {
                fastScrolling = true
                startScrollTimer()
            }
        } else {
            restartScrollTimer();
        }

        updateScrollState({offset: scrollOffset, fastScrolling: fastScrolling})
    }

    return <Box sx={{display: 'flex', flexDirection: 'column', height: "100%"}}
                className={`${scrollState.fastScrolling ? 'mv-scrolling' : ''}`}>
        <WeekDayHeader visibleDate={visibleDate}/>
        <Box sx={{flexGrow: 1}}>
            <AutoSizer>
                {({height, width}) => {
                    return <VariableSizeList
                        ref={virtualizedListRef}
                        height={height}
                        width={width}
                        itemCount={monthList.length}
                        overscanCount={2}
                        estimatedItemSize={averageMonthHeight}
                        itemSize={getMonthHeight}
                        initialScrollOffset={initialMonthIndex * averageMonthHeight}
                        onItemsRendered={handleOnItemsRendered}
                        onScroll={handleOnScroll}>
                        {renderMonth}
                    </VariableSizeList>
                }}
            </AutoSizer>
        </Box>
    </Box>;
}

/*
 * The head of the Planner view that contains the week day names
 */
const WeekDayHeader = function (props) {
    const {visibleDate} = props

    return <Box sx={{display: 'flex', flexDirection: 'row', borderBottom: '1px solid #BBB'}}>
        <Box sx={{flex: '1 0', display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
            <MonthSelector visibleDate={visibleDate}/>
        </Box>
        <Box fontSize='small' sx={{flex: '7 0', display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
            {
                dayOfWeek.map((day, i) => (
                    <span className="mv-header-day" key={i}>{day}</span>)
                )
            }
        </Box>
    </Box>
}

/*
 * Month component it handles all the events in the month
 */
const Month = memo(function (props) {
    const {month, year, contentData, gridData, isLoading, style} = props;
    const {renderDayCell} = usePlanner();
    const days = useMemo(() => monthVerticalVisibleDays(month, year), [month, year]);
    const weeks = useMemo(() => chunk(days, 7), [days]);
    const weekContents = useMemo(
        () => {
            // check the content length because contentData should be an array, but the value provided by the DataLoader
            // is a hash resourceId -> content. Here I should add the selected resource id, so we can pick the correct
            // content. But if the contentData is an array then the selected resource id is not needed.
            const contents = (contentData !== null && contentData !== undefined && contentData.length > 0) ? contentData : emptyArray;
            return weeks.reduce((acc, w) => {
                acc.push(getVisibleContents(contents, w[0], w[w.length - 1]))
                return acc
            }, [])
        },
        [contentData, weeks]
    );

    return <div className="mv-month" style={style}>
        <MonthLabel month={month} year={year} isLoading={isLoading}/>
        <div className="mv-month-content">
            <div className={"mv-month-popup"}>
                {`${months[month].label} ${year}`}
            </div>
            {
                weeks.map((w, i) => (
                    <Week key={i}
                          days={w}
                          renderDayCell={renderDayCell}
                          gridData={gridData}
                          events={weekContents[i]}
                    />))
            }
        </div>
    </div>
})

// Month Label: indicate month name and year and the loading status
const MonthLabel = memo(function (props) {
    const {month, year, isLoading} = props

    return <div className={`mv-month-label ${month % 2 === 0 ? null : 'mv-grid-bg-grey'}`}>
        <div className={"mv-aside-div"}/>
        <Typography>{months[month].label}</Typography>
        <Typography>{year.toString().slice(-2)}</Typography>
        <div className={"mv-aside-div"}>{isLoading && <CircularProgress size={20}/>}</div>
    </div>
})

// Week component: it has a grid as background and the content as foreground
const Week = memo(function (props) {
    const {days, events, gridData, renderDayCell} = props;

    return <div className="mv-week">
        {<WeekGrid days={days} gridData={gridData} renderDayCell={renderDayCell}/>}
        {<WeekContent days={days} events={events}/>}
    </div>
})

// The grid of a single week
const WeekGrid = memo(function (props) {
    const {days, gridData, renderDayCell} = props;

    const dayClasses = (d) => {
        let classes = 'mv-day-grid';

        if (getMonth(d) % 2 !== 0) {
            classes += ' mv-grid-bg-grey'
        }
        if (inFirstSevenDaysOfMonth(d)) {
            classes += ' mv-day-grid-first-7'
        }
        if (isSaturday(d)) {
            classes += ' mv-grid-bg-highlighted'
        }
        return classes;
    }

    return <div className='mv-week-grid'>
        {
            days.map((d, i) => (
                <div key={i} className={dayClasses(d)}>
                    {renderDayCell ? renderDayCell(formatISO(d, {representation: 'date'}), gridData) : null}
                </div>
            ))
        }
    </div>
})

// The content of a row
const WeekContent = memo(function (props) {
    const {days, events} = props;

    const firstDate = startOfDay(days[0]);
    const lastDate = startOfDay(add(days[6], {days: 1}));
    const segments = getSegments(sortEvents(events), firstDate, lastDate);

    return <div className="mv-week-content">
        <div className='mv-row'>
            {days.map((d, i) => <DateNumber key={i}
                                            dayNumber={d.getDate().toString()}
                                            isToday={isToday(d)}
                                            isRed={isSunday(d)}
            />)}
        </div>
        <div className='mv-row'>
            {segments.map(s => <RowSegment key={s.segmentId} segment={s}/>)}
        </div>
    </div>
})

// Day number
const DateNumber = memo(function (props) {
    const {dayNumber, isToday, isRed} = props;

    return <div className='mv-day-number-container'>
        <span
            className={`mv-day-number ${isRed ? 'mv-day-number-sunday' : ''} ${isToday ? 'mv-day-number-today' : ''}`}>
            {dayNumber}
        </span>
    </div>
})

// A segment that may span over more days containing either an event or representing just an offset
const RowSegment = function (props) {
    const {segment} = props;
    const {renderEvent} = usePlanner();

    const width = 100.0 * segment.lengthFraction;

    return <div style={{
        flexBasis: `${width}%`,
        maxWidth: `${width}%`,
        height: `${segmentHeight}px`,
        maxHeight: `${segmentHeight}px`,
        overflow: 'hidden'
    }}>
        {!!segment.event && renderEvent(segment.event, segment)}
    </div>
}

export default MonthVerticalView;